'use client';

/**
 * Binds PostHog to the auth session and gates session recording to the
 * authenticated surfaces. Must render inside the next-auth SessionProvider
 * (root layout). Renders nothing.
 *
 * Identity: on sign-in, identify() with the opaque auth id (never email
 * or name) plus role/entity properties, and group() by builder or
 * subcontractor entity so usage rolls up per customer account — the unit
 * that matters in a B2B product. Users with no entity (internal staff)
 * get their groups cleared. Guarded by a change key, not the session
 * object: next-auth refetches the session on every window focus and
 * returns a fresh object each time, so keying on identity would re-fire
 * identify()/group() on every alt-tab.
 *
 * Recording: starts only when BOTH hold — a signed-in session AND an
 * authenticated surface (/portal, /admin, /onboarding — the same prefixes
 * the middleware protects). It stops when either ends, so a signed-in
 * user browsing the public marketing pages is not recorded there; the
 * marketing site is measured in aggregate only. See posthog.ts for the
 * masking posture inside recordings.
 *
 * On sign-out, reset() drops the stored distinct id so a subsequent login
 * on a shared machine cannot chain two people into one profile.
 */

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { isPostHogActive, posthog } from '@/lib/analytics/posthog';

const RECORDED_PATH_PREFIXES = ['/portal', '/admin', '/onboarding'];

export function PostHogIdentity() {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  // Change key of the last identify()/group() call — id, entity, and role
  // together, so an entity relink or role change re-syncs while ordinary
  // session refetches do not.
  const identityKey = useRef<string | null>(null);

  useEffect(() => {
    if (!isPostHogActive()) return;
    if (status === 'loading') return;

    const user = session?.user;
    if (user?.id) {
      const key = `${user.id}|${user.builderId ?? ''}|${user.subcontractorId ?? ''}|${user.role ?? ''}`;
      if (identityKey.current !== key) {
        identityKey.current = key;

        const entityType = user.builderId
          ? 'builder'
          : user.subcontractorId
            ? 'subcontractor'
            : 'internal';
        posthog.identify(user.id, {
          role: user.role ?? 'unknown',
          entity_type: entityType,
        });

        if (user.builderId) {
          posthog.group('builder', `builder:${user.builderId}`);
        } else if (user.subcontractorId) {
          posthog.group('subcontractor', `subcontractor:${user.subcontractorId}`);
        } else {
          // No entity (internal staff, or just unlinked): drop any group
          // carried over from a previous state so events stop rolling up
          // under a stale account.
          posthog.resetGroups();
        }
      }
    } else if (identityKey.current) {
      // Signed out in this pageview: detach identity.
      identityKey.current = null;
      posthog.reset();
    }
  }, [session, status]);

  useEffect(() => {
    if (!isPostHogActive()) return;
    if (status === 'loading') return;

    const onRecordedSurface = RECORDED_PATH_PREFIXES.some((prefix) =>
      pathname?.startsWith(prefix)
    );
    const shouldRecord = Boolean(session?.user?.id) && onRecordedSurface;

    if (shouldRecord && !posthog.sessionRecordingStarted()) {
      posthog.startSessionRecording();
    } else if (!shouldRecord && posthog.sessionRecordingStarted()) {
      posthog.stopSessionRecording();
    }
  }, [session, status, pathname]);

  return null;
}
